import { FC, useEffect, useMemo, useState } from 'react';

import { useActiveAccount } from '@dbt-labs/app-nav';
import {
  AgenticChat,
  InputActionsContainer,
  useAgentMessageContext,
} from '@dbt-labs/copilot-ui';
import {
  Button,
  Drawer,
  DrawerContent,
  MenuButton,
  MenuItem,
  RyeconColorDbtCopilot,
  RyeconDbtCopilot,
  RyeconPlus,
  RyeconProjects,
  Sizes,
} from '@dbt-labs/sourdough';

import { useCopilotPaneContext } from '@/context/CopilotPaneProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useFeatureFlags } from '@/context/FeatureFlagProvider';
import { useEnvsForCopilot } from '@/hooks/admin/environments';
import { useRawParams } from '@/hooks/params';

const useCopilotEnabled = () => {
  const { ai447CatalogAgenticChat } = useFeatureFlags();
  const { copilotLockedUntil } = useActiveAccount().data ?? {};
  const { hasAccountFeature } = useDbtCloudAccount();

  return useMemo(
    () =>
      !!ai447CatalogAgenticChat &&
      !copilotLockedUntil &&
      hasAccountFeature('aiFeatures'),
    [ai447CatalogAgenticChat, copilotLockedUntil, hasAccountFeature],
  );
};

export const CopilotButton: FC = () => {
  const isEnabled = useCopilotEnabled();
  const { openCopilotPane } = useCopilotPaneContext();

  return isEnabled ? (
    <Button
      text="Copilot"
      type="secondary"
      onClick={openCopilotPane}
      ryecon={RyeconDbtCopilot}
      size={Sizes.sm}
    />
  ) : null;
};

export const CopilotDrawer: FC = () => {
  const isEnabled = useCopilotEnabled();
  const { appendMessage, messageHistory, resetMessageHistory } =
    useAgentMessageContext();
  const { copilotPaneIsOpen, closeCopilotPane } = useCopilotPaneContext();
  const { accountId, dbtCloudProjects, userId } = useDbtCloudAccount();
  const { projectId, environmentType } = useRawParams();
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>();
  const projectEnvs = useEnvsForCopilot({
    accountId,
    projectId: selectedProjectId,
  });

  useEffect(() => {
    if (selectedProjectId === undefined && projectId !== undefined) {
      setSelectedProjectId(projectId);
    }
  }, [projectId, selectedProjectId]);

  const selectedProject = useMemo(
    () => (dbtCloudProjects ?? []).find((proj) => proj.id === selectedProjectId),
    [dbtCloudProjects, selectedProjectId],
  );

  const environmentId: number | undefined = useMemo(() => {
    const projectEnvsData = projectEnvs.data;
    if (!selectedProjectId || !projectEnvsData) {
      return;
    }

    const envId =
      selectedProject?.defaultEnvironmentId ??
      projectEnvsData.production?.id ??
      projectEnvsData.development?.id ??
      projectEnvsData.staging?.id;

    // If the user is on a route for a project/env combo and selected that project, we should respect that.
    if (selectedProjectId === projectId && environmentType) {
      return projectEnvsData[environmentType]?.id ?? envId;
    }

    return envId;
  }, [
    environmentType,
    projectEnvs.data,
    projectId,
    selectedProject?.defaultEnvironmentId,
    selectedProjectId,
  ]);

  const menuItems: MenuItem[] = useMemo(
    () =>
      (dbtCloudProjects ?? []).map((proj) => ({
        label: proj.name,
        value: proj.id,
      })),
    [dbtCloudProjects],
  );

  if (!isEnabled) {
    return null;
  }

  return (
    <Drawer isOpen={copilotPaneIsOpen} onClose={closeCopilotPane} resizable>
      {(onClose) => (
        <DrawerContent
          onClose={onClose}
          title={
            <span className="flex items-center gap-2">
              <RyeconColorDbtCopilot /> Copilot
            </span>
          }
          className="h-full"
          actions={[
            {
              buttonProps: {
                ariaLabel: 'Start a new chat',
                ryecon: RyeconPlus,
                type: 'tertiary',
              },
              onClick: resetMessageHistory,
              text: null,
              tooltip: 'Start new chat',
            },
          ]}
        >
          <AgenticChat
            agent="data_discovery"
            appName="catalog"
            appendMessage={appendMessage}
            messageHistory={messageHistory}
            accountId={accountId ?? 0}
            environmentId={environmentId ?? 0}
            projectId={selectedProjectId ?? 0}
            userId={userId === 'loading' ? 0 : userId}
            submitIsDisabled={!selectedProjectId}
            inputActionsContainer={
              <InputActionsContainer>
                <MenuButton
                  label={selectedProject?.name ? undefined : 'Select a project'}
                  text={selectedProject?.name ?? 'Select a project'}
                  ryecon={RyeconProjects}
                  items={menuItems}
                  onClick={(item) => {
                    setSelectedProjectId(item as number);
                  }}
                  size={Sizes.xs}
                  menuPlacement="top"
                />
              </InputActionsContainer>
            }
          />
        </DrawerContent>
      )}
    </Drawer>
  );
};
